import { Statistic2DisplayType } from './../statistic2-types';
import { DetectDevicesService } from '@fi-shared/fi-shared-services';
import {
    Component,
    OnDestroy,
    OnInit,
    AfterViewInit,
    ViewChild,
    Renderer2,
    ChangeDetectionStrategy,
    ElementRef,
    Input,
    ViewEncapsulation,
} from '@angular/core';
import { Statistic2HelperService } from '@fi-app/statistic2/statistic2-helper.service';
import { Statistic2Service } from '@fi-app/statistic2/statistic2.service';
import { LoadingService } from '@fi-app/shared/fi-loading/loading.service';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { ViewportRuler } from '@angular/cdk/overlay';
import { CdkDropList, CdkDropListGroup, moveItemInArray, CdkDragEnter } from '@angular/cdk/drag-drop';
import { FiScrollToElementAnimateService } from '@fi-shared/fi-shared-services/scroll-to-element-animate.service';
import { FiDialogsService } from '@fi-shared/fi-dialogs';
import { AppTextService } from '@fi-shared/app-text';
import { FiCommonSharedService } from '@fi-shared/fi-shared-services/fi-common-shared.service';
import { ObjectTypes } from '@fi-shared/types';

@Component({
    selector: 'fi-statistic2-dashboard',
    templateUrl: './statistic2-dashboard.component.html',
    styleUrls: ['./statistic2-dashboard.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false,
})
export class Statistic2DashboardComponent implements OnInit, OnDestroy, AfterViewInit {
    @Input() reloadData: boolean;

    // workaround source https://stackblitz.com/edit/angular-ivy-k1vasn
    // angular material iisue https://github.com/angular/components/issues/13372

    @ViewChild(CdkDropListGroup) listGroup: CdkDropListGroup<CdkDropList>;
    @ViewChild('listGroup') listGroupElRef: ElementRef;
    @ViewChild(CdkDropList) placeholder: CdkDropList;
    @ViewChild('dragPlaceholder') dragPlaceholderElRef: ElementRef;

    isItemDropped$: Subject<void> = new Subject<void>();
    public target: CdkDropList;
    public targetIndex: number;
    public source: CdkDropList;
    public sourceIndex: number;

    constructor(
        private statistic2Service: Statistic2Service,
        private statistic2HelperService: Statistic2HelperService,
        private fiLoading: LoadingService,
        private viewportRuler: ViewportRuler,
        private renderer: Renderer2,
        private elRef: ElementRef<HTMLElement>,
        private scrollToElementAnimateService: FiScrollToElementAnimateService,
        private detectDeviceService: DetectDevicesService,
        private fiDialogsService: FiDialogsService,
        private commonService: FiCommonSharedService,
        private appTextService: AppTextService,
    ) {}

    get reports$() {
        return this.statistic2Service.dashboardReports$;
    }

    /*get testIds() {
        if (!this.statistic2Service.dashboardReports$.value)
            return [];
        return this.statistic2Service.dashboardReports$.value.map(x => x.reportParameter.Id);
    }*/

    ngOnInit() {
        new Promise<void>((resolve, reject) => {
            if (!this.reports$.value) {
                this.statistic2Service.hasReportParameters().then((result) => {
                    if (result) {
                        resolve();
                    } else {
                        this.fiDialogsService.notify({
                            yesButtonText: this.appTextService.get('common.close'),
                            messageText: this.appTextService.get('statistic.generate_reports_text'),
                            headerText: this.appTextService.get('statistic.generate_reports_header'),
                        });
                        this.statistic2Service.generateDefaultReportParameters().then(resolve, reject);
                    }
                }, reject);
            } else {
                resolve();
            }
        }).then(() => {
            if (!this.reports$.value || this.reloadData) {
                this.statistic2Service.loadDashboardItems();
            } else {
                // animate charts even if we do not reload reports data
                const currentReports = this.reports$.value;
                this.reports$.next(null);
                setTimeout(() => this.reports$.next(currentReports));
            }

            this.statistic2Service.reportParameterDashboardAdded.pipe(takeUntil(this.ngOnDestroy$)).subscribe(() => {
                // scroll to bottom
                const scrollableElement = this.elRef.nativeElement.parentElement;
                this.scrollToElementAnimateService.scrollToY(scrollableElement, scrollableElement.scrollHeight);
            });
        });
    }

    ngAfterViewInit() {
        const phElement = this.placeholder.element.nativeElement;

        phElement.style.display = 'none';
        phElement.parentNode.removeChild(phElement);
    }

    dropped() {
        if (!this.target) return;

        const parent: HTMLElement = this.listGroupElRef.nativeElement;
        const phElement: HTMLElement = this.placeholder.element.nativeElement;
        const phElementIndex = Array.from(parent.children).indexOf(phElement);

        phElement.style.display = 'none';
        parent.removeChild(phElement);
        parent.appendChild(phElement);

        parent.insertBefore(this.source.element.nativeElement, parent.children[this.sourceIndex]);

        if (this.sourceIndex !== phElementIndex) {
            moveItemInArray(this.reports$.value, this.sourceIndex, phElementIndex);
            this.updateSortIndexes();
            this.isItemDropped$.next();
        }

        this.target = null;
        this.targetIndex = undefined;
        this.source = null;
        this.sourceIndex = undefined;
    }

    entered($event: CdkDragEnter) {
        const phElement: HTMLElement = this.placeholder.element.nativeElement;
        const dropElement: HTMLElement = $event.container.element.nativeElement;
        const prevTarget: CdkDropList = this.target;
        const prevTargetIndex: number = this.targetIndex;
        this.target = $event.container;

        const dropElementIsTheSource: boolean = !dropElement.parentNode;
        const prevAndCurrentTargetAreTheSame: boolean = this.target === prevTarget;
        if (dropElementIsTheSource || prevAndCurrentTargetAreTheSame) {
            return;
        }

        this.targetIndex = Array.from(dropElement.parentNode.children).indexOf(dropElement);

        if (!this.source) {
            this.source = $event.item.dropContainer;
            this.sourceIndex = Array.from(dropElement.parentNode.children).indexOf(
                $event.item.dropContainer.element.nativeElement,
            );
            const sourceElement: HTMLElement = this.source.element.nativeElement;

            this.fixPhElementStyling(phElement, sourceElement);

            sourceElement.parentNode.removeChild(sourceElement);
        }

        const index: number = prevTargetIndex ?? this.sourceIndex;
        const insertAfter: boolean = index < this.targetIndex;

        this.listGroupElRef.nativeElement.insertBefore(phElement, insertAfter ? dropElement.nextSibling : dropElement);
    }

    dragReleased() {
        const phElementPositionWasChanged: boolean = !!this.source;
        if (phElementPositionWasChanged) {
            this.dragPlaceholderElRef.nativeElement.style.transform = 'none';
            this.dragPlaceholderElRef.nativeElement.parentNode.removeChild(this.dragPlaceholderElRef.nativeElement);
            this.placeholder.element.nativeElement.appendChild(this.dragPlaceholderElRef.nativeElement);
        }
    }

    private fixPhElementStyling(phElement: HTMLElement, sourceElement: HTMLElement) {
        phElement.style.width = sourceElement.clientWidth - 6 + 'px';
        phElement.style.height = sourceElement.clientHeight - 6 + 'px';

        const size = Array.from(sourceElement.classList).find((c) => c.startsWith('content-item-c'));

        phElement.style.display = '';
        const oldSize = Array.from(phElement.classList).find((c) => c.startsWith('content-item-c'));
        if (oldSize) {
            phElement.classList.remove(oldSize);
        }
        if (size) {
            phElement.classList.add(size);
        }
    }

    isMobilePhone() {
        return this.detectDeviceService.isMobilePhone();
    }

    isFullWidthWidget(report) {
        if (report.reportData) {
            return (
                report.reportData.Data.length > 20 &&
                report.reportParameter.DisplayType !== Statistic2DisplayType.PieChart
            );
        }
        return false;
    }

    private updateSortIndexes() {
        const reportParameters = this.reports$.value.map((item) => item.reportParameter);
        this.commonService.updateSortIndexes(reportParameters, ObjectTypes.Statistic2);
    }

    ngOnDestroy$ = new Subject<void>();
    ngOnDestroy() {
        this.ngOnDestroy$.next();
        this.ngOnDestroy$.complete();
    }
}
